import { ArrowRightOutlined, DeleteOutlined, EditOutlined, ExportOutlined } from '@ant-design/icons';
import { AUDIT_ENTITY_TYPES, canAdminister, canEditAnySubmission, isWithinEditWindow } from '@cult/domain';
import type { PublicPraise } from '@cult/schemas';
import { Link, useNavigate } from '@tanstack/react-router';
import { App, Button, Popconfirm, Space, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import { BodyRender, DrawerQueryStatus, NavDrawer, UserAvatar } from '../../../../../components';
import { FORM_ORIGINS } from '../../../../../lib/form-origins';
import { useCurrentUser } from '../../../../../state/use-current-user';
import { AttachmentsView } from '../../../../attachments';
import { EditHistory } from '../../../../audit';
import { useDeletePraise, usePraise } from '../../../../praises';
import { ReferenceTags } from '../../../../reference';
import styles from './AdminPraiseDrawer.module.scss';

const { Text } = Typography;

type Props = {
  praiseId: string | null;
  onClose: () => void;
  praises?: PublicPraise[];
  onNavigate?: (id: string) => void;
};

export const AdminPraiseDrawer = ({ praiseId, onClose, praises = [], onNavigate }: Props) => (
  <NavDrawer
    open={!!praiseId}
    onClose={onClose}
    navKeys={praises.map((p) => String(p.number))}
    currentKey={praiseId}
    onNavigate={onNavigate}
  >
    {praiseId ? <DrawerContent praiseId={praiseId} onClose={onClose} /> : null}
  </NavDrawer>
);

const DrawerContent = ({ praiseId, onClose }: { praiseId: string; onClose: () => void }) => {
  const praiseQuery = usePraise(praiseId);
  const role = useCurrentUser((s) => s.user?.role);

  if (!praiseQuery.data || praiseQuery.isError) return <DrawerQueryStatus query={praiseQuery} entity="Praise" />;

  const praise = praiseQuery.data;

  return (
    <>
      <DrawerHeader praise={praise} onClose={onClose} />

      <div className={styles.body}>
        <section className={styles.section}>
          <div className={styles.sectionLabel}>Behavior</div>
          <Text>{praise.behavior}</Text>
        </section>
        {role && canAdminister(role) ? (
          <section className={styles.section}>
            <div className={styles.sectionLabel}>Edit history</div>
            <EditHistory entityType={AUDIT_ENTITY_TYPES.PRAISE} entityId={praise.id} authorId={praise.author.id} />
          </section>
        ) : null}
      </div>
    </>
  );
};

const DrawerHeader = ({ praise, onClose }: { praise: PublicPraise; onClose: () => void }) => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const deletePraise = useDeletePraise();
  const role = useCurrentUser((s) => s.user?.role);
  const inWindow = isWithinEditWindow(praise.createdAt);
  // Admins edit at any time; delete stays inside the window for everyone.
  const editable = inWindow || (role !== undefined && canEditAnySubmission(role));

  const handleDelete = () => {
    deletePraise.mutate(praise, {
      onSuccess: () => {
        message.success('Praise deleted');
        onClose();
      },
      onError: (err) => message.error(err.message),
    });
  };

  return (
    <header className={styles.header}>
      <div className={styles.metaRow}>
        <div className={styles.metaLeft}>
          <UserAvatar size={20} avatarUrl={praise.author.avatarUrl} />
          <Text style={{ fontSize: 13 }}>{praise.author.displayName}</Text>
          <ArrowRightOutlined style={{ fontSize: 11 }} />
          <UserAvatar size={20} avatarUrl={praise.receiver.avatarUrl} />
          <Text style={{ fontSize: 13 }}>{praise.receiver.displayName}</Text>
        </div>
        <div className={styles.metaRight}>
          <Tooltip title={dayjs(praise.createdAt).format('MMM D, YYYY h:mm A')}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {dayjs(praise.createdAt).fromNow()}
            </Text>
          </Tooltip>
        </div>
      </div>

      <div className={styles.titleRow}>
        <h2 className={styles.title}>{praise.title}</h2>
        <Space size="small">
          {editable ? (
            <Tooltip title="Edit">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                aria-label="Edit praise"
                onClick={() =>
                  navigate({
                    to: '/praises/$praiseId/edit',
                    params: { praiseId: String(praise.number) },
                    search: { origin: FORM_ORIGINS.ADMIN },
                  })
                }
              />
            </Tooltip>
          ) : null}
          {inWindow ? (
            <Popconfirm
              title="Delete this praise?"
              description="This also removes its comments, attachments, and awarded points."
              okText="Delete"
              okButtonProps={{ danger: true }}
              onConfirm={handleDelete}
            >
              <Tooltip title="Delete">
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  aria-label="Delete praise"
                  loading={deletePraise.isPending}
                />
              </Tooltip>
            </Popconfirm>
          ) : null}
          <Link to="/praise/$id" params={{ id: String(praise.number) }} target="_blank" rel="noreferrer">
            <Tooltip title="Open detail page">
              <Button type="text" size="small" icon={<ExportOutlined />} aria-label="Open detail page" />
            </Tooltip>
          </Link>
        </Space>
      </div>

      <ReferenceTags tags={praise} style={{ marginTop: 12 }} />

      <div className={styles.description}>
        <div className={styles.sectionLabel}>What happened</div>
        <BodyRender source={praise.whatHappened} />
      </div>

      {praise.attachments.length > 0 ? (
        <div className={styles.attachments}>
          <AttachmentsView attachments={praise.attachments} />
        </div>
      ) : null}
    </header>
  );
};
